import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  OwcTable,
  OwcTableHeader,
  OwcTableHeaderCell,
  OwcTableBody,
  OwcTableRow,
  OwcTableCell,
  OwcComponentHeader,
  OwcIcon,
  OwcInput,
  OwcButton,
  OwcComponentFooter,
  OwcTypography,
  OwcSkeletonLoader,
} from '@one/react';
import { useGroupData } from '../../../contexts/GroupDataContext';
import { downloadJSON, downloadCSV } from '../../../utils/downloadUtils';
import { isLatestOnlyGroup } from '../../../utils/getDataGroupPath';
import { getSchemaItems } from '../../../utils/general/schemaEntity';
import { escapeRegExp } from '../../../utils/general/textSearch';
import DashboardInsights from '../../Common/DashboardsInsights/DashboardInsights';
import { useNavigate } from 'react-router-dom';

function TablePanel({
  schemaData,
  schemaLoading,
  schemaError,
  schemaSource,
  loadingReleaseChange,
}) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [rowStats, setRowStats] = useState({ total: 0, filtered: 0 });
  const { releaseVersion, groupData } = useGroupData();
  const tablePanelRef = useRef(null);
  const schemaTitle = schemaData?.name || '—';

  // Scroll to the top of the table panel when a new schema is selected
  useEffect(() => {
    if (schemaSource && tablePanelRef.current) {
      // Walk up to find the nearest scrollable ancestor and reset its scroll
      let el = tablePanelRef.current.parentElement;
      while (el) {
        if (el.scrollHeight > el.clientHeight) {
          el.scrollTo({ top: 0, behavior: 'smooth' });
        }
        el = el.parentElement;
      }
    }
  }, [schemaSource]);

  // For download filenames: use releaseVersion (Lab) or the item's own version (DiabetesCare)
  const downloadVersion = useMemo(() => {
    const useItemVersion = isLatestOnlyGroup(groupData?.groupName);
    if (!useItemVersion && releaseVersion) return releaseVersion;
    if (!schemaSource?.name || !schemaSource?.type) return '';
    const list = getSchemaItems(groupData, schemaSource.type);
    const item = Array.isArray(list) ? list.find((e) => e.name === schemaSource.name) : null;
    return item?.version || '';
  }, [releaseVersion, schemaSource, groupData]);
  const defKey = useMemo(
    () =>
      schemaTitle
        .replace('IGW_', 'Igw')
        .split(/(?=[A-Z])/)
        .join('-')
        .toLowerCase(),
    [schemaTitle]
  );

  const definition = useMemo(() => {
    return (
      schemaData?.schema?.$defs?.[defKey] ||
      (schemaData?.schema?.type === 'object' ? schemaData.schema : null)
    );
  }, [schemaData, defKey]);

  const toggle = (key) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const highlight = (text = '', term = '') => {
    if (!term) return text;
    const safeTerm = escapeRegExp(term);
    const regex = new RegExp(`(${safeTerm})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} style={{ backgroundColor: '#ffff00' }}>
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const getTypeLabel = (value) => {
    if (!value) return '—';

    const plain =
      value.type === 'array'
        ? value.items?.type === 'object' && value.items?.title
          ? `array of ${value.items.title}`
          : `array of ${value.items?.type || ''}`
        : Array.isArray(value.type)
          ? value.type.join(' or ')
          : value.type === 'object' && value.title
            ? value.title
            : value.type || '—';

    // underline clickable types
    const isRef =
      (value.type === 'object' && value.title) || (value.type === 'array' && value.items?.title);

    const targetName = value.type === 'object' ? value.title : value.items?.title;

    const styled = highlight(plain, searchTerm);

    if (isRef && targetName) {
      return (
        <span
          style={{
            color: '#2e62b3',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
          onClick={() => {
            const cdt = groupData?.complexDataTypes?.find((c) => c.name === targetName);

            const segments = [
              groupData?.groupName,
              'complexDataType',
              ...(cdt?.parent ? cdt.parent.split('/') : []),
              targetName,
            ]
              .filter(Boolean)
              .map(encodeURIComponent)
              .join('/');
            navigate(`/dashboard/${segments}`);
          }}
        >
          {styled}
        </span>
      );
    }

    return styled;
  };

  const getTypeTextPlain = (value) => {
    if (!value) return '—';

    // if it's an array _and_ items.title exists, use that first
    if (value.type === 'array') {
      const item = value.items || {};
      // PREFER item.title over item.type
      const label = item.title || item.type || '';
      return `array of ${label}`;
    }

    // if it's an object _and_ has a title, use that
    if (value.type === 'object' && value.title) {
      return value.title;
    }

    // arrays of primitives or unions
    if (Array.isArray(value.type)) {
      return value.type.join(' or ');
    }

    // everything else (string, number, date‑time, etc.)
    return `${value.type || ''}${value.format ? `: ${value.format}` : ''}`;
  };
  const getConstraints = (value) => {
    if (!value) return '—';
    const parts = [];

    // For arrays, also inspect items-level constraints
    const target = value.type === 'array' && value.items ? value.items : value;

    if (target.const !== undefined) parts.push(`Const: ${target.const}`);
    if (Array.isArray(target.enum)) parts.push(`Enum: [${target.enum.join(', ')}]`);
    if (target.format) parts.push(`Format: ${target.format}`);
    if (target.pattern) parts.push(`Pattern: ${target.pattern}`);
    if (target.minLength !== undefined) parts.push(`minLength: ${target.minLength}`);
    if (target.maxLength !== undefined) parts.push(`maxLength: ${target.maxLength}`);
    if (target.minimum !== undefined) parts.push(`minimum: ${target.minimum}`);
    if (target.maximum !== undefined) parts.push(`maximum: ${target.maximum}`);
    if (target.minItems !== undefined) parts.push(`minItems: ${target.minItems}`);
    if (target.maxItems !== undefined) parts.push(`maxItems: ${target.maxItems}`);
    if (target.uniqueItems) parts.push('uniqueItems: true');

    return parts.length > 0 ? parts.join(', ') : '—';
  };
  const renderRows = (properties, requiredProps = [], path = '$', type) => {
    let total = 0;
    let filtered = 0;
    const rows = [];

    const sortedKeys = Object.keys(properties).sort((a, b) => {
      if (a === 'id') return -1;
      if (b === 'id') return 1;
      if (a === 'type') return -1;
      if (b === 'type') return 1;
      return 0;
    });

    sortedKeys.forEach((key) => {
      const value = properties[key];
      const propPath = `${path}.${key}${value.type === 'array' ? '[*]' : ''}`;
      const isRequired = requiredProps?.includes(key);
      const description = value.description || '';

      const displayType = getTypeLabel(value);
      const typeText = getTypeTextPlain(value);
      const constraintsText = getConstraints(value);
      const searchableText = `${propPath} ${typeText} ${constraintsText} ${description}`;

      const hasSubProps =
        (value.type === 'object' && value.properties) ||
        (value.type === 'array' && value.items?.type === 'object' && value.items.properties);

      let childRows = [];
      let childCounts = { total: 0, filtered: 0 };

      // Always recurse to children, regardless of collapse or match
      if (hasSubProps) {
        const subProps = value.type === 'array' ? value.items.properties : value.properties;
        const subRequired = value.type === 'array' ? value.items.required : value.required;
        const nested = renderRows(subProps, subRequired, propPath, type);
        childRows = nested.rows;
        childCounts = { total: nested.total, filtered: nested.filtered };
      }

      // Match this row
      const matchesSearch =
        !searchTerm || searchableText.toLowerCase().includes(searchTerm.toLowerCase());

      total += 1 + childCounts.total;
      if (matchesSearch) {
        filtered += 1;
        rows.push(
          <React.Fragment key={propPath}>
            <OwcTableRow>
              {/* ...cells as before... */}
              <OwcTableCell valign="middle">
                {highlight(propPath, searchTerm)}
                {isRequired && (
                  <span style={{ color: '#ed6c02', fontWeight: 'bold', marginLeft: 2 }}>*</span>
                )}
                {type !== 'ComplexDatatypes' && hasSubProps && (
                  <OwcIcon
                    name={collapsed[propPath] ? 'chevron_down' : 'chevron_up'}
                    style={{
                      marginLeft: 5,
                      position: 'relative',
                      top: 5,
                      cursor: 'pointer',
                    }}
                    onClick={() => toggle(propPath)}
                  />
                )}
              </OwcTableCell>
              <OwcTableCell>{displayType}</OwcTableCell>
              <OwcTableCell>
                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', fontSize: '12px' }}>
                  {highlight(constraintsText, searchTerm)}
                </div>
              </OwcTableCell>
              <OwcTableCell>
                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {highlight(description, searchTerm)}
                </div>
              </OwcTableCell>
            </OwcTableRow>
            {/* children are rendered only if expanded */}
            {!collapsed[propPath] && childRows}
          </React.Fragment>
        );
        filtered += childCounts.filtered;
      } else {
        // Even if parent doesn't match, add all child matches!
        rows.push(...childRows);
        filtered += childCounts.filtered;
      }
    });

    return { rows, total, filtered };
  };

  const schemaRows = useMemo(() => {
    if (!definition?.properties) return { rows: [], total: 0, filtered: 0 };
    const result = renderRows(definition.properties, definition.required, '$', schemaSource?.type);
    setRowStats({ total: result.total, filtered: result.filtered });
    return result;
  }, [definition, searchTerm, collapsed]);

  if (schemaError) {
    return (
      <owc-banner
        status="error"
        style={{ margin: '24px auto', maxWidth: '720px' }}
        class="sc-owc-banner-h owc-banner"
        role="alert"
        direction="horizontal"
        hydrated="true"
      >
        Error loading schema: {schemaError}
      </owc-banner>
    );
  }

  if (!schemaLoading && !definition?.properties && schemaSource?.name) {
    return (
      <div style={{ padding: '16px', color: '#666' }}>
        No schema to show for the {schemaSource?.type}: <strong>{schemaSource?.name}</strong>
      </div>
    );
  }

  if (!schemaData && !schemaLoading && !schemaSource) {
    if (loadingReleaseChange) {
      return null;
    }
    return <DashboardInsights groupData={groupData} />;
  }

  return (
    <div ref={tablePanelRef} style={{ overflowX: 'auto', maxWidth: '100%' }}>
      {/* Description and comment */}
      <div
        style={{
          marginTop: '16px',
          marginBottom: '16px',
          padding: '0 3px',
          color: '#333',
        }}
      >
        {schemaLoading ? (
          <>
            <OwcSkeletonLoader variant="text" style={{ height: '14px', width: '40%' }} />
            <OwcSkeletonLoader
              variant="text"
              style={{ height: '14px', width: '60%', marginTop: 8 }}
            />
          </>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              marginBottom: 8,
              gap: 24,
            }}
          >
            <div style={{ flex: 2 }}>
              {schemaData?.description && (
                <div>
                  <strong>Description:</strong> <span>{schemaData.description}</span>
                </div>
              )}
              <strong>JSON Schema draft version:</strong>{' '}
              <a
                href={schemaData?.schema?.$schema}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1a5fd6', textDecoration: 'underline' }}
              >
                {schemaData?.schema?.$schema || '—'}
              </a>
              {schemaSource?.type !== 'complexDataType' &&
                !isLatestOnlyGroup(groupData?.groupName) &&
                schemaSource?.name &&
                (() => {
                  const list = getSchemaItems(groupData, schemaSource.type);
                  const item = Array.isArray(list)
                    ? list.find(
                        (e) =>
                          e.name === schemaSource.name && e.parent === (schemaSource.parent || '')
                      ) || list.find((e) => e.name === schemaSource.name)
                    : null;
                  const parentParts = item?.parent ? item.parent.split('/') : [];

                  return (
                    <div style={{ display: 'flex' }}>
                      <strong>Type:</strong>{' '}
                      <span style={{ paddingLeft: '4px' }}>
                        {[
                          'com.roche.standards',
                          groupData?.groupName,
                          ...parentParts,
                          schemaSource.name,
                        ]
                          .filter(Boolean)
                          .join('.')
                          .toLowerCase()}
                      </span>
                    </div>
                  );
                })()}
            </div>
            <div style={{ flex: 2 }}>
              {schemaSource?.type !== 'complexDataType' &&
                schemaSource?.name &&
                (() => {
                  const list = getSchemaItems(groupData, schemaSource.type);
                  const item = Array.isArray(list)
                    ? list.find(
                        (e) =>
                          e.name === schemaSource.name && e.parent === (schemaSource.parent || '')
                      ) || list.find((e) => e.name === schemaSource.name)
                    : null;
                  const parentParts = item?.parent ? item.parent.split('/') : [];
                  if (parentParts.length === 0) return null;
                  return (
                    <div style={{ marginBottom: 6 }}>
                      {parentParts.map((part, i) => (
                        <div key={i}>
                          <strong>Sub Data Group {i + 1}:</strong> <span>{part}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              {schemaData?.schema?.$comment && (
                <div>
                  <strong>Comment:</strong> <span>{schemaData.schema.$comment}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {schemaSource?.type === 'complexDataType' && (
        <div
          style={{
            marginTop: '12px',
            padding: '12px 14px',
            background: '#fff4e5',
            border: '1px solid #ffa726',
            borderRadius: '6px',
            color: '#8a4b00',
            fontSize: '12px',
            lineHeight: 1.5,
            marginBottom: '12px',
          }}
        >
          <strong>Warning:</strong> This complex datatype definition is there for documentation
          purposes only, it is not intended to be implemented as is. The complex datatype will not
          be used in its original form instead it is optimised based on the event requirements so
          please refer to the event constituting it for its implementation. The complex datatypes
          alone (without an event) are not subjected/allowed to implement.
        </div>
      )}
      {/* Schema Table */}
      <OwcTable
        rounded
        verticalBorder
        size="small"
        style={{
          minHeight: '280px',
          tableLayout: 'auto',
          minWidth: '900px',
        }}
      >
        <OwcTableHeader style={{ overflowX: 'hidden' }} sticky>
          <OwcTableHeaderCell width="300px" align="left" resizable>
            Property path
          </OwcTableHeaderCell>
          <OwcTableHeaderCell width="150px" align="left" resizable>
            Type
          </OwcTableHeaderCell>
          <OwcTableHeaderCell width="200px" align="left" resizable>
            Constraints
          </OwcTableHeaderCell>
          <OwcTableHeaderCell width="auto" align="left" resizable>
            Description (from Schema)
          </OwcTableHeaderCell>
        </OwcTableHeader>

        <OwcTableBody>
          {schemaLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <OwcTableRow key={`skeleton-${i}`}>
                {[60, 40, 40, 80].map((w, j) => (
                  <OwcTableCell key={j}>
                    <OwcSkeletonLoader variant="text" style={{ width: `${w}%`, height: '13px' }} />
                  </OwcTableCell>
                ))}
              </OwcTableRow>
            ))
          ) : schemaRows.rows.length === 0 ? (
            <OwcTableRow>
              <OwcTableCell
                colSpan={100}
                style={{
                  padding: '50px 0',
                  border: 0,
                  background: '#fffbe6',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '80px',
                    width: '100%',
                  }}
                >
                  <span
                    style={{
                      color: '#a00',
                      fontWeight: 500,
                      textAlign: 'center',
                      lineHeight: 1.6,
                      maxWidth: '85%',
                      margin: '0 auto',
                    }}
                  >
                    No matching results found. Search is limited to the columns:&nbsp;
                    <span style={{ color: '#222', fontWeight: 700 }}>Property Path</span>,{' '}
                    <span style={{ color: '#222', fontWeight: 700 }}>Type</span>,{' '}
                    <span style={{ color: '#222', fontWeight: 700 }}>Constraints</span>, and{' '}
                    <span style={{ color: '#222', fontWeight: 700 }}>Description</span>.
                  </span>
                </div>
              </OwcTableCell>
            </OwcTableRow>
          ) : (
            schemaRows.rows
          )}
        </OwcTableBody>

        {/* Table header and download */}
        <OwcComponentHeader slot="header" status="positive">
          <span
            style={{
              maxWidth: '450px',
              gap: '10px',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {schemaTitle}
          </span>
          <div
            slot="suffix"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'nowrap',
            }}
          >
            <div style={{ color: '#555', minWidth: '140px' }}>
              {searchTerm
                ? `Filtered ${rowStats.filtered} of ${rowStats.total} rows`
                : `Total rows: ${rowStats.total}`}
            </div>
            {schemaSource?.type !== 'complexDataType' && (
              <OwcButton
                title={`Download ${schemaTitle} version ${downloadVersion} as JSON`}
                variant="default"
                onClick={() => {
                  const fileName = `${schemaTitle}${downloadVersion ? `_${downloadVersion}` : ''}.schema.json`;
                  downloadJSON(schemaData.schema, fileName);
                }}
                disabled={schemaLoading}
                style={{ whiteSpace: 'nowrap' }}
              >
                <OwcIcon slot="prefix" name="cloud_download" family="outlined" />
                JSON Schema
              </OwcButton>
            )}

            <OwcButton
              title={`Download ${schemaTitle} version ${downloadVersion} as CSV`}
              variant="default"
              onClick={() => {
                const fileName = `${schemaTitle}${downloadVersion ? `_${downloadVersion}` : ''}.schema.csv`;
                downloadCSV(schemaData.schema, fileName);
              }}
              disabled={schemaLoading}
            >
              <OwcIcon slot="prefix" name="cloud_download" family="outlined" />
              CSV
            </OwcButton>

            <OwcInput
              variant="outlined"
              placeholder="Search"
              size="small"
              rounded
              onInput={(e) => setSearchTerm(e.target.value)}
              disabled={schemaLoading}
              value={searchTerm}
              onClearClick={() => setSearchTerm('')}
            >
              <OwcIcon slot="prefix" name="search" family="outlined" />
            </OwcInput>
          </div>
        </OwcComponentHeader>

        <div slot="footer" style={{ display: 'flex', flexDirection: 'column', padding: 0 }}>
          <OwcComponentFooter style={{ padding: '8px 12px' }}>
            <OwcTypography slot="prefix" id="selected-row"></OwcTypography>
            <OwcTypography slot="suffix">
              {searchTerm
                ? `Filtered ${rowStats.filtered} of ${rowStats.total} rows`
                : `Total rows: ${rowStats.total}`}
            </OwcTypography>
          </OwcComponentFooter>
        </div>
      </OwcTable>
    </div>
  );
}

export default TablePanel;
